// src/pages/OrdiniMaterialiPage.js
// Area Personale: ogni utente crea e consulta esclusivamente le proprie richieste di
// materiale al magazzino. Il magazzino le riceve e gestisce da Acquisti → Richieste Magazzino.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2 } from "lucide-react";
import { NuovaRichiestaMagazzinoModal } from "./MagazzinoPage";

const STATO_CONFIG = {
  "Da inviare":  { label:"Da inviare",     colore:"#9ca3af", bg:"#f3f4f6", icon:"📝" },
  in_attesa:     { label:"In attesa",      colore:"#d97706", bg:"#fff7ed", icon:"⏳" },
  in_lavorazione:{ label:"In lavorazione", colore:"#2563eb", bg:"#eff6ff", icon:"🔄" },
  parziale:      { label:"In evasione",    colore:"#7c3aed", bg:"#ede9fe", icon:"⚠️" },
  chiusa:        { label:"Chiusa",         colore:"#059669", bg:"#d1fae5", icon:"✅" },
  annullata:     { label:"Annullata",      colore:"#9ca3af", bg:"#f3f4f6", icon:"❌" },
};

export default function OrdiniMaterialiPage() {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [richieste, setRichieste] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editRichiesta, setEditRichiesta] = useState(null);

  useEffect(() => {
    if (!effectiveOrgId || !currentUser) return;
    return onSnapshot(
      query(collection(db,"richiesteMateriali"), where("organizationId","==",effectiveOrgId), where("richiestaFrom","==",currentUser.uid)),
      snap => {
        const lista = snap.docs.map(d=>({id:d.id,...d.data()}));
        lista.sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0));
        setRichieste(lista);
      }
    );
  }, [effectiveOrgId, currentUser]);

  const elimina = async (id) => {
    if (!await confirm("Eliminare questa bozza?", { type:"danger" })) return;
    await deleteDoc(doc(db,"richiesteMateriali",id));
    addToast("Bozza eliminata","info");
  };

  const apriRiga = (r) => {
    const statoRichiesta = r.statoRichiesta || "Inviata"; // richieste vecchie senza il campo = già inviate
    if (statoRichiesta === "Da inviare") setEditRichiesta(r);
    // Se già inviata: nessuna azione, sola consultazione dello stato nella riga stessa
  };

  return (
    <div style={{padding:20, height:"100%", overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:6,flexWrap:"wrap"}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"#1e1b4b",margin:0}}>📦 Ordini materiali al Magazzino</h3>
        <button onClick={()=>setShowModal(true)} className="btn btn-primary"
          style={{display:"flex",alignItems:"center",gap:5,background:"#7c3aed",borderColor:"#7c3aed",padding:"5px 12px",fontSize:12}}>
          <Plus size={13}/> Nuova richiesta
        </button>
        <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{richieste.length} richiesta/e create da te</span>
      </div>
      <p style={{fontSize:11.5,color:"#9ca3af",marginBottom:16}}>
        Qui vedi solo le richieste che hai creato tu — mai quelle degli altri. Una bozza resta modificabile ed è visibile al magazzino solo dopo averla inviata.
      </p>

      {richieste.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📦</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna richiesta creata da te per ora</div>
          <div style={{fontSize:12,marginTop:4}}>Crea la prima richiesta di materiale per una commessa</div>
        </div>
      ) : (
        <div>
          {richieste.map(r => {
            const statoRichiesta = r.statoRichiesta || "Inviata";
            const isBozza = statoRichiesta === "Da inviare";
            const statoCfg = isBozza ? STATO_CONFIG["Da inviare"] : (STATO_CONFIG[r.stato] || STATO_CONFIG.in_attesa);
            return (
              <div key={r.id} style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:isBozza?"pointer":"default"}}
                onClick={()=>apriRiga(r)}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>
                    {r.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{r.codiceCommessa}]</span>}
                    {r.projectName||"—"}
                  </div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                    {(r.righe||[]).length} material{(r.righe||[]).length===1?"e":"i"}
                    {r.urgenza==="urgente" && <span style={{marginLeft:8,color:"#dc2626",fontWeight:700}}>🚨 Urgente</span>}
                  </div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:statoCfg.bg,color:statoCfg.colore,fontWeight:700,flexShrink:0}}>
                  {statoCfg.icon} {statoCfg.label}
                </span>
                {isBozza && (
                  <button onClick={e=>{e.stopPropagation();elimina(r.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:4}}>
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <NuovaRichiestaMagazzinoModal orgId={effectiveOrgId} currentUser={currentUser} onClose={()=>setShowModal(false)}/>
      )}
      {editRichiesta && (
        <NuovaRichiestaMagazzinoModal orgId={effectiveOrgId} currentUser={currentUser} richiestaEsistente={editRichiesta} onClose={()=>setEditRichiesta(null)}/>
      )}
    </div>
  );
}
